import {
  DetailRequirement,
  GeneralResourceDetail,
  MpxResponseError,
  ResourceDetail,
  UserDetailsAPI,
  UserResourceDetailsQueryParams,
} from '@cibo/core'
import { logRequestError } from '@cibo/ui'
import { UseMutationOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { QUERY_KEY } from '../queries'

export const PROFILE_USER_DETAILS_QUERY_KEY = QUERY_KEY.USER_DETAILS

export const useUserDetails = <T extends GeneralResourceDetail = GeneralResourceDetail>({
  detailRequirements,
  resourceId,
}: {
  detailRequirements?: DetailRequirement<T>[]
  resourceId?: string
}) =>
  useQuery({
    queryKey: [QUERY_KEY.USER_DETAILS, resourceId, detailRequirements],
    queryFn: () =>
      !!resourceId
        ? UserDetailsAPI.get<T>({ resourceId }).catch(error =>
            logRequestError(error, { query: 'useUserDetails' })
          )
        : Promise.reject('missing resourceId'),
    select: response =>
      response?.details?.filter(({ traitId, year }) =>
        detailRequirements
          ? !!detailRequirements.find(req => req.traitId === traitId && req.year === year)
          : true
      ),
    enabled: !!resourceId,
  })

export const useUpdateUserDetails = <T extends ResourceDetail = ResourceDetail>(
  props?: Pick<
    UseMutationOptions<
      unknown,
      AxiosError<MpxResponseError>,
      UserResourceDetailsQueryParams<T>,
      unknown
    >,
    'onSuccess' | 'onError'
  >
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (update: UserResourceDetailsQueryParams<T>) => UserDetailsAPI.update(update),
    onError: (error: any, variables, context) => {
      props?.onError && props.onError(error, variables, context)
      logRequestError(error, { query: 'useUpdateUserDetails', variables, context })
      return error
    },
    onSuccess: (response: unknown, update: UserResourceDetailsQueryParams<T>, context: unknown) => {
      //@todo update the query data instead of invalidating
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.USER_DETAILS, update.userId] })
      props?.onSuccess && props?.onSuccess(response, update, context)
    },
  })
}

export const useDeleteUserDetails = (
  props?: Pick<
    UseMutationOptions<
      unknown,
      AxiosError<MpxResponseError>,
      UserResourceDetailsQueryParams,
      unknown
    >,
    'onSuccess' | 'onError'
  >
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: UserResourceDetailsQueryParams) => UserDetailsAPI.delete(request),
    onError: (error: any, variables, context) => {
      props?.onError && props.onError(error, variables, context)
      logRequestError(error, { query: 'useUpdateUserDetails', variables, context })
      return error
    },
    onSuccess: (response: unknown, update: UserResourceDetailsQueryParams, context: unknown) => {
      //@todo update the query data instead of invalidating
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.USER_DETAILS, update.userId] })
      props?.onSuccess && props?.onSuccess(response, update, context)
    },
  })
}
